import React from 'react';
import { Clock, MapPin, ShoppingCart, X } from 'lucide-react';
import { formatMoney } from '../../data/cemeteries';
import { getHoldExpiry, getPlot, getSelection } from '../../lib/plotStore';
import { formatCountdown, useTick } from '../../lib/useStore';

const BURIAL_LABEL = { burial: 'Burial', cremation: 'Cremation' };

/**
 * The "your seats" rail. Mirrors a theatre checkout: what you hold, what it
 * costs, and how long before the hold lapses and the sites go back on sale.
 */
const SelectionPanel = ({ cemetery, onRelease, onExtend, onCheckout, onClearAll }) => {
  const selection = getSelection(cemetery.id);
  const expiresAt = getHoldExpiry(cemetery.id);

  // Only tick while a hold is actually running.
  useTick(Boolean(expiresAt));

  const remaining = expiresAt ? expiresAt - Date.now() : 0;
  const expiringSoon = remaining > 0 && remaining < 2 * 60 * 1000;

  const plots = selection.map((id) => getPlot(cemetery, id));
  const subtotal = plots.reduce((sum, p) => sum + p.price, 0);
  const careFee = Math.round(subtotal * cemetery.pricing.careFeeRate);
  const total = subtotal + careFee;

  if (selection.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 text-navy-900">
          <ShoppingCart className="h-5 w-5" />
          <h3 className="font-cormorant text-2xl font-semibold">Your selection</h3>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          Choose an available site on the map to begin. Selected sites are held for you for 15
          minutes so no one else can claim them while you decide.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 p-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-navy-900">
            <ShoppingCart className="h-5 w-5" />
            <h3 className="font-cormorant text-2xl font-semibold">
              Your selection ({selection.length})
            </h3>
          </div>
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs text-slate-500 underline underline-offset-2 hover:text-navy-800"
          >
            Clear all
          </button>
        </div>

        {/* Hold countdown */}
        <div
          className={`mt-4 flex items-center justify-between rounded-md border px-3 py-2 ${
            expiringSoon
              ? 'border-amber-300 bg-amber-50 text-amber-800'
              : 'border-slate-200 bg-slate-50 text-slate-600'
          }`}
        >
          <span className="flex items-center gap-2 text-xs">
            <Clock className="h-4 w-4" />
            Held for you — <strong className="tabular-nums">{formatCountdown(remaining)}</strong>
          </span>
          <button
            type="button"
            onClick={onExtend}
            className="text-xs font-medium text-navy-800 underline underline-offset-2 hover:text-navy-900"
          >
            Extend
          </button>
        </div>
      </div>

      {/* Line items */}
      <ul className="divide-y divide-slate-100">
        {plots.map((plot) => (
          <li key={plot.id} className="flex items-center gap-3 px-6 py-3">
            <MapPin className="h-4 w-4 shrink-0 text-slate-300" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800">Site {plot.id}</p>
              <p className="text-xs text-slate-500">
                Row {plot.row}, No. {plot.number} · {BURIAL_LABEL[plot.burialType]}
              </p>
            </div>
            <span className="text-sm tabular-nums text-slate-700">
              {formatMoney(plot.price)}
            </span>
            <button
              type="button"
              onClick={() => onRelease(plot.id)}
              className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-navy-800"
              aria-label={`Remove site ${plot.id} from your selection`}
            >
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>

      {/* Totals */}
      <div className="space-y-2 border-t border-slate-100 px-6 py-4 text-sm">
        <div className="flex justify-between text-slate-600">
          <span>Sites ({plots.length})</span>
          <span className="tabular-nums">{formatMoney(subtotal)}</span>
        </div>
        <div className="flex justify-between text-slate-600">
          <span>Perpetual care</span>
          <span className="tabular-nums">{formatMoney(careFee)}</span>
        </div>
        <div className="flex justify-between border-t border-slate-100 pt-2 text-base font-semibold text-navy-900">
          <span>Total</span>
          <span className="tabular-nums">{formatMoney(total)}</span>
        </div>
      </div>

      <div className="px-6 pb-6">
        <button
          type="button"
          onClick={onCheckout}
          className="w-full rounded-md bg-navy-800 py-3 text-sm font-medium tracking-wide text-white transition hover:bg-navy-900"
        >
          Continue to checkout
        </button>
        <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-500">
          Sites are released automatically when the hold expires.
        </p>
      </div>
    </div>
  );
};

export default SelectionPanel;
